"use client";
import { useAuth } from "@/hook/useAuth";
import { useAppSelector } from "@/store/hooks";
import { checkCountry } from "@/utils/Constants";
import { formatCountryUrl } from "@/utils/formatCountryUrl";
import Image from "next/image";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { destinationsLinks, navbarSearchIconProtectedRoutes } from "./Helper";
import { HamBurgerMenu } from "./Icons";
import SmallScreenNabBar from "./SmallScreenNabBar";
import SmallNavBar from "../newHomepage/SmallNavBar";
import { ChevronDown } from "lucide-react";

export function NewNavBar() {
  const params = useParams();

  const { isAnonymous } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlug, setCurrentSlug] = useState("");

  // States for controlling hover card visibility
  const [aboutUsOpen, setAboutUsOpen] = useState(false);
  const [destinationsOpen, setDestinationsOpen] = useState(false);
  const [universitiesOpen, setUniversitiesOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [protectedRoute, setProtectedRoute] = useState(false);
  const [openActivity, setOpenActivity] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollRef = useRef(0);
  const hiddenRef = useRef(false);
  const tickingRef = useRef(false);
  const pathName = usePathname();

  useEffect(() => {
    hiddenRef.current = hidden;
  }, [hidden]);

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem("activityPopup");

    if (!hasSeenPopup) {
      setOpenActivity(true);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.setItem("activityPopup", "true");
    setOpenActivity(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!tickingRef.current) {
        window.requestAnimationFrame(() => {
          const current = window.scrollY;
          const shouldHide = current > lastScrollRef.current && current > 200;
          if (shouldHide !== hiddenRef.current) {
            setHidden(shouldHide);
          }
          lastScrollRef.current = current;
          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (pathName) {
      const parts = pathName.split("/");
      const slug = parts[1];
      setCurrentSlug(slug);
    }
  }, [pathName]);

  const { countryList } = useAppSelector((state) => state.country);

  const filteredArr = useMemo(() => {
    if (!Array.isArray(countryList) || countryList.length === 0) return [];

    const countryMap = new Map(
      destinationsLinks.map((item) => [item.name.toLowerCase(), item.flag]),
    );
    const labelMap = new Map(
      destinationsLinks.map((item) => [item.name.toLowerCase(), item.label]),
    );
    return countryList.map((country) => {
      let countryName = country.country_name?.toLowerCase() || "";
      if (countryName.includes(" - ")) {
        countryName = countryName.split(" - ")[1].trim();
      }
      return {
        ...country,
        label: labelMap.get(countryName) || null,
        flag: countryMap.get(countryName) || null,
      };
    });
  }, [countryList]);

  const navBarOpenHandler = () => {
    setIsOpen(true);
    document.body.classList.add("!overflow-hidden");
  };

  // Close all hover cards when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest("[data-hover-card]")) {
        setAboutUsOpen(false);
        setDestinationsOpen(false);
        setUniversitiesOpen(false);
        setProductsOpen(false);
        setServicesOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // HANDLE SCROLLING
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY >= window.innerHeight) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const removeOverFlow = () => {
    document.body.classList.remove("!overflow-hidden");
  };

  useEffect(() => {
    navbarSearchIconProtectedRoutes.map((item) => {
      if (item === currentSlug.trimStart()) {
        setProtectedRoute(true);
      } else {
        setProtectedRoute(false);
      }
    });
  }, [currentSlug]);
  const showActivityBar = !openActivity;

  return (
    <>
      <header
        role="banner"
        className={`left-0 w-full bg-[linear-gradient(180deg,_#000000_0%,_rgba(217,_217,_217,_0)_85.07%)] z-[50] duration-300 ease-in-out ${hidden ? "-top-[100%]" : "top-0"} ${params.countryParams ? "static" : "fixed"}`}
      >
        <div className="container 2xl:max-w-[1628px] py-[13px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                href="/"
                onClick={removeOverFlow}
                className="max-w-[100px] sm:max-w-[110px] md:max-w-[140px] betaRibbon"
              >
                <Image
                  priority
                  className="w-full h-full hidden sm:block"
                  src="/logo/svg/whiteLogo.svg"
                  alt="logo"
                  width={135}
                  height={30}
                />
                <Image
                  priority
                  className="w-full h-full sm:hidden"
                  src="/logo/svg/whiteLogo.svg"
                  alt="logo"
                  width={80}
                  height={30}
                />
              </Link>
            </div>

            {/* NAV-BAR NAVIGATION LINKS */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-9 2xl:gap-[48px]">
              {/* Destinations */}
              <HoverCard
                open={destinationsOpen}
                onOpenChange={setDestinationsOpen}
              >
                <HoverCardTrigger
                  data-hover-card
                  onClick={() => setDestinationsOpen(!destinationsOpen)}
                  className={`ff_poppins text-white font-normal text-[14px] xl:text-[16px] 2xl:text-[18px] group duration-300 ease-in-out relative before:content-[''] hover:text-white before:absolute before:h-[5px] before:bottom-[-4px] before:left-0 before:w-full cursor-pointer ${currentSlug === "all-countries" || currentSlug === "country" ? "nav_links_stroke_active" : ""
                    }`}
                >
                  <button
                    className={`group-hover:text-theme_pink flex items-center gap-3 ${currentSlug === "all-countries" || currentSlug === "country" ? "text-theme_pink" : ""
                      }`}
                  >
                    Study Global
                    <span className="group-hover:rotate-180 duration-500 ease-in-out">
                      <ChevronDown className="text-white" />
                    </span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent className="w-full z-[111] rounded-[12px]  border border-[#ECECEC]">
                  <div className="overflow-y-auto h-full max-h-[calc(100vh-200px)]">
                    <p className="ff_poppins text-sm font-medium text-[#9D9DA1]">
                      POPULAR DESTINATIONS
                    </p>
                    <div className="flex flex-wrap w-[470px] h-auto duration-300">
                      {filteredArr.map((entry, idx) => {
                        return (
                          <Link
                            key={entry?.country_id || `country-${idx}`}
                            href={`/country/${formatCountryUrl(entry.country_name)}`}
                            className="capitalize ff_poppins flex gap-4 items-center font-normal text-[17px] px-[16px] py-[12px] text-center hover:text-primary w-1/2 "
                            onClick={() => {
                              setDestinationsOpen(false);
                              removeOverFlow();
                            }}
                          >
                            <span className="inline-block w-full max-w-[38px] rounded-md overflow-hidden">
                              {entry.flag}
                            </span>
                            <div className="flex items-start flex-col">
                              <span>
                                {(entry?.country_name).toLocaleLowerCase() ===
                                  checkCountry
                                  ? "USA"
                                  : entry?.country_name
                                    .toLocaleLowerCase()
                                    .replaceAll("europe -", "")}
                              </span>
                              <span className="text-[#9D9DA1] text-[10px] font-medium ff_poppins">
                                {entry?.label}
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <Link
                href="/chat"
                className={`ff_poppins font-normal text-white text-[14px] xl:text-[16px] hover:underline 2xl:text-[18px] group duration-300 ease-in-out relative before:content-[''] before:absolute before:h-[5px] before:bottom-[-4px] before:left-0 before:w-full cursor-pointer hover:text-white ${currentSlug === "chat" ? "underline" : ""
                  }`}
                onClick={() => {
                  setAboutUsOpen(false);
                  removeOverFlow();
                }}
              >
                EdviaBot
              </Link>

              <HoverCard open={aboutUsOpen} onOpenChange={setAboutUsOpen}>
                <HoverCardTrigger
                  data-hover-card
                  onClick={() => setAboutUsOpen(!aboutUsOpen)}
                  className={`ff_poppins font-normal text-white text-[14px] xl:text-[16px] 2xl:text-[18px] group duration-300 ease-in-out relative before:content-[''] before:absolute before:h-[5px] before:bottom-[-4px] before:left-0 before:w-full hover:text-white cursor-pointer ${currentSlug === "about"
                    ? "nav_links_stroke_active"
                    : ""
                    }`}
                >
                  <button
                    className={`group-hover:text-white cursor-pointer flex items-center gap-3 ${currentSlug === "about-us"
                      ? "text-white"
                      : ""
                      }`}
                  >
                    Company
                    <span className="group-hover:rotate-180 duration-500 ease-in-out">
                      <ChevronDown className="text-white" />
                    </span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent className="w-full z-[111]">
                  <div className="flex flex-col w-[130px] h-auto bg-white">
                    <Link
                      href="/about-us"
                      className="ff_poppins font-medium text-base pb-[6px] hover:text-primary transition-colors duration-200 flex flex-col items-start"
                      onClick={() => {
                        setAboutUsOpen(false);
                        removeOverFlow();
                      }}
                    >
                      About Us
                      <span className="ff_poppins text-xs font-medium text-[#9D9DA1]">
                        Mission & team
                      </span>
                    </Link>
                    <Link
                      href="/for-institutions"
                      className="ff_poppins font-medium text-base pb-[6px] mt-[6px] hover:text-primary transition-colors duration-200  flex flex-col items-start"
                      onClick={() => {
                        setAboutUsOpen(false);
                        removeOverFlow();
                      }}
                    >
                      For Institutions
                      <span className="ff_poppins text-xs font-medium text-[#9D9DA1]">
                        Partner with Edvia
                      </span>
                    </Link>
                    <Link
                      href="/latest-news"
                      className="ff_poppins font-medium text-base py-[6px] hover:text-primary transition-colors duration-200 flex flex-col items-start"
                      onClick={() => {
                        setAboutUsOpen(false);
                        removeOverFlow();
                      }}
                    >
                      News
                      <span className="ff_poppins text-xs font-medium text-[#9D9DA1]">
                        Announcements
                      </span>
                    </Link>
                    <Link
                      href="/blogs"
                      className="ff_poppins font-medium text-base py-[6px] hover:text-primary transition-colors duration-200 flex flex-col items-start"
                      onClick={() => {
                        setAboutUsOpen(false);
                        removeOverFlow();
                      }}
                    >
                      Blogs
                      <span className="ff_poppins text-xs font-medium text-[#9D9DA1]">
                        Study abroad guides
                      </span>
                    </Link>
                    <Link
                      href="/careers"
                      className="ff_poppins font-medium text-base py-[6px] hover:text-primary transition-colors duration-200 flex flex-col items-start"
                      onClick={() => {
                        setAboutUsOpen(false);
                        removeOverFlow();
                      }}
                    >
                      Careers
                      <span className="ff_poppins text-xs font-medium text-[#9D9DA1]">
                        Join our team
                      </span>
                    </Link>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <Link
                href="/book-exam"
                className={`ff_poppins font-normal text-white text-[14px] xl:text-[16px] hover:underline 2xl:text-[18px] group duration-300 ease-in-out relative before:content-[''] before:absolute before:h-[5px] before:bottom-[-4px] before:left-0 before:w-full cursor-pointer hover:text-white ${currentSlug === "chat" ? "underline" : ""
                  }`}
                onClick={() => {
                  setAboutUsOpen(false);
                  removeOverFlow();
                }}
              >
                Book Exam
              </Link>
            </div>

            <div className="flex items-center gap-2 sm:gap-5">
              {isAnonymous ? (
                <div className="flex items-center gap-[10px] sm:gap-[18px]">
                  <Link
                    href={"/auth"}
                    className="sm:py-[11px] py-2 px-4 sm:px-[18px] xl:px-[44.5px] rounded-full bg-primary-gradient font-medium text-white text-xs sm:text-base ff_poppins inline-block overflow-hidden relative shinyAnimation before:bg-shiny-gradient before:content-[''] before:h-[200%] before:absolute before:top-[-55%] before:z-0 before:rotate-[-70deg] before:w-[80px] before:right-[calc(100%+20px)] hover:scale-[1.05] focus:scale-[1.05] duration-300 ease-in-out"
                  >
                    <span className="relative z-20 hidden sm:inline">Login/SignUp</span>
                    <span className="relative z-20 sm:hidden whitespace-nowrap">Get Started Free</span>
                  </Link>
                </div>
              ) : (
                <div className="flex items-center gap-[10px] sm:gap-[18px]">
                  <Link
                    href={"/dashboard"}
                    className="sm:py-[11px] py-2 px-4 sm:px-[18px] 2xl:px-[44.5px] rounded-full bg-primary-gradient font-medium text-white text-xs sm:text-base ff_poppins inline-block overflow-hidden relative shinyAnimation before:bg-shiny-gradient before:content-[''] before:h-[200%] before:absolute before:top-[-55%] before:z-0 before:rotate-[-70deg] before:w-[80px] before:right-[calc(100%+20px)] hover:scale-[1.05] focus:scale-[1.05] duration-300 ease-in-out"
                  >
                    <span className="relative z-20 hidden sm:inline">Go to Dashboard</span>
                    <span className="relative z-20 sm:hidden whitespace-nowrap">Dashboard</span>
                  </Link>
                </div>
              )}

              {/* Hamburger Icon on Right (Mobile/Tablet Only) */}
              <span
                className="cursor-pointer lg:hidden w-[38px] h-[38px] rounded-[14px] bg-white/10 border border-white/15 flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                onClick={navBarOpenHandler}
              >
                <HamBurgerMenu color="white" />
              </span>
            </div>
          </div>
        </div>
      </header>
      {showActivityBar && <div className="h-[20px] sm:h-[44px]" />}
      {/* NAV-BAR UNDER TABLET */}
      <SmallNavBar setIsOpen={setIsOpen} isOpen={isOpen} />
      {isOpen && (
        <span
          onClick={() => {
            setIsOpen(false);
            document.body.classList.remove("!overflow-hidden");
          }}
          className="bg-black/60 fixed top-0 left-0 w-full h-full z-50"
        ></span>
      )}
    </>
  );
}
